import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import PropertyCard from '../components/common/PropertyCard';
import { getProperties } from '../services/api';
import { DEMO_PROPERTIES, CITIES, PROPERTY_TYPES } from '../utils/constants';
import './Properties.css';

const Properties = ({ category }) => {
  const [searchParams] = useSearchParams();
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    city: searchParams.get('city') || '',
    type: searchParams.get('type') || '',
    bedrooms: '',
    listingType: '',
  });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [sortBy, setSortBy] = useState('newest');

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getProperties();
      const data = res.data?.data?.content || res.data?.data || res.data || [];
      setAllProperties(Array.isArray(data) ? data : []);
    } catch {
      // Backend offline — show demo data
      setAllProperties(DEMO_PROPERTIES);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProperties(); }, [fetchProperties]);

  const filtered = useMemo(() => {
    let list = allProperties;
    if (filters.city)       list = list.filter(p => p.city === filters.city);
    if (filters.type)       list = list.filter(p => p.propertyType === filters.type);
    if (filters.bedrooms)   list = list.filter(p => String(p.bedrooms) === filters.bedrooms);
    if (filters.listingType) list = list.filter(p => p.listingType === filters.listingType);
    if (sortBy === 'price-asc')  list = [...list].sort((a,b) => (a.price||0) - (b.price||0));
    if (sortBy === 'price-desc') list = [...list].sort((a,b) => (b.price||0) - (a.price||0));
    return list;
  }, [allProperties, filters, sortBy]);

  const setF   = (k, v) => setFilters(p => ({...p, [k]: v}));
  const clear  = () => setFilters({ city:'', type:'', bedrooms:'', listingType:'' });
  const active = Object.values(filters).filter(Boolean).length;
  const title  = category === 'projects' ? 'Developer Projects' : 'All Properties';

  return (
    <div className="pl-page">

      {/* Hero — padding-top clears 70px fixed navbar */}
      <div className="pl-hero">
        <div className="container">
          <span className="section-label">{category === 'projects' ? 'Our Developments' : 'Browse Listings'}</span>
          <h1 className="section-title on-dark">{title}</h1>
          <p className="pl-hero__sub">{category === 'projects'
            ? 'Premium residential & commercial developments in prime locations'
            : 'Curated listings across Noida, Gurgaon and Dholera Smart City'}</p>
        </div>
      </div>

      {/* Sticky toolbar */}
      <div className="pl-toolbar-bar">
        <div className="container">
          <div className="pl-toolbar">
            {/* Desktop filters */}
            <div className="pl-filters">
              <select className="pl-sel" value={filters.city} onChange={e=>setF('city',e.target.value)}>
                <option value="">All Cities</option>
                {CITIES.map(c=><option key={c}>{c}</option>)}
              </select>
              <select className="pl-sel" value={filters.type} onChange={e=>setF('type',e.target.value)}>
                <option value="">All Types</option>
                {PROPERTY_TYPES.map(t=><option key={t}>{t}</option>)}
              </select>
              <select className="pl-sel" value={filters.bedrooms} onChange={e=>setF('bedrooms',e.target.value)}>
                <option value="">Any BHK</option>
                <option value="1">1 BHK</option><option value="2">2 BHK</option>
                <option value="3">3 BHK</option><option value="4">4 BHK</option><option value="5">5+</option>
              </select>
              <select className="pl-sel" value={filters.listingType} onChange={e=>setF('listingType',e.target.value)}>
                <option value="">Listing Type</option>
                <option value="Sale">Sale</option><option value="Rent">Rent</option><option value="New Launch">New Launch</option>
              </select>
              {active > 0 && (
                <button className="pl-clear" onClick={clear}><X size={12}/> Clear ({active})</button>
              )}
            </div>

            <div className="pl-toolbar-right">
              <span className="pl-count">{filtered.length} {filtered.length===1?'property':'properties'}</span>
              <select className="pl-sel" value={sortBy} onChange={e=>setSortBy(e.target.value)}>
                <option value="newest">Newest First</option>
                <option value="price-asc">Price: Low-High</option>
                <option value="price-desc">Price: High-Low</option>
              </select>
              <button className="pl-mobile-btn" onClick={()=>setShowMobileFilters(p=>!p)}>
                <SlidersHorizontal size={14}/> Filters {active>0 && <span className="pl-badge">{active}</span>}
              </button>
            </div>
          </div>

          {/* Mobile filter panel */}
          {showMobileFilters && (
            <div className="pl-mobile-panel">
              <div className="pl-mobile-grid">
                <div className="pl-mg"><label>City</label>
                  <select value={filters.city} onChange={e=>setF('city',e.target.value)}>
                    <option value="">All</option>{CITIES.map(c=><option key={c}>{c}</option>)}
                  </select></div>
                <div className="pl-mg"><label>Type</label>
                  <select value={filters.type} onChange={e=>setF('type',e.target.value)}>
                    <option value="">All</option>{PROPERTY_TYPES.map(t=><option key={t}>{t}</option>)}
                  </select></div>
                <div className="pl-mg"><label>BHK</label>
                  <select value={filters.bedrooms} onChange={e=>setF('bedrooms',e.target.value)}>
                    <option value="">Any</option><option value="1">1</option><option value="2">2</option>
                    <option value="3">3</option><option value="4">4</option><option value="5">5+</option>
                  </select></div>
                <div className="pl-mg"><label>Listing</label>
                  <select value={filters.listingType} onChange={e=>setF('listingType',e.target.value)}>
                    <option value="">All</option><option value="Sale">Sale</option>
                    <option value="Rent">Rent</option><option value="New Launch">New Launch</option>
                  </select></div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="pl-body">
        <div className="container">
          {loading ? (
            <div className="pl-empty"><div className="pl-empty-icon">⏳</div><h3>Loading properties...</h3></div>
          ) : filtered.length === 0 ? (
            <div className="pl-empty">
              <div className="pl-empty-icon">🏠</div>
              <h3>No properties found</h3>
              <p>Try adjusting your filters</p>
              <button className="btn-primary" onClick={clear} style={{marginTop:'16px'}}>Clear Filters</button>
            </div>
          ) : (
            <div className="pl-grid">
              {filtered.map(p => <PropertyCard key={p.id} property={p}/>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Properties;
